import { useEffect, useMemo, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

import { InfoAlert } from '../InfoAlertField/InfoAlertField';
import { useTheme } from '@mui/material/styles';

type FieldValues = Record<string, string>;

type RadioField = {
  name: string;
  label: string;
  type: string;
  options?: string[];
  default?: string;
  disabled?: boolean;
  placeholder?: string;
  description?: string;
};

type OptionType = {
  label: string;
  value: string;
  fields?: RadioField[];
  uiOptions?: {
    infoText?: string;
    checkboxLabel?: string;
  };
};

function parseAndValidateFutureDate(value: string): {
  valid: boolean;
  message?: string;
} {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return { valid: true }; // optional field
  }

  const formatRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-[0-9]{4}$/;
  if (!formatRegex.test(trimmed)) {
    return {
      valid: false,
      message: 'Error. Inputted text is an invalid format.',
    };
  }

  const [dayStr, monthStr, yearStr] = trimmed.split('-');
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  const parsedDate = new Date(year, month - 1, day);

  const isRealDate =
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getDate() === day;

  if (!isRealDate) {
    return {
      valid: false,
      message: 'Error. Inputted text is an invalid format.',
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);

  if (parsedDate <= today) {
    return {
      valid: false,
      message:
        'Error. Inputted date has already passed. Enter a valid future date.',
    };
  }

  return { valid: true };
}

const PasswordField = ({
  label,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  error,
  helperText,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}) => (
  <TextField
    label={label}
    type="password"
    value={value ?? ''}
    onChange={e => onChange(e.target.value)}
    onBlur={onBlur}
    fullWidth
    size="medium"
    disabled={disabled}
    required={required}
    error={error}
    helperText={helperText}
  />
);

const FieldInput = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  description,
  required,
}: {
  field: RadioField;
  value?: string;
  onChange: (name: string, value: string) => void;
  onBlur?: (name: string) => void;
  error?: boolean;
  helperText?: string;
  description?: string;
  required?: boolean;
}) => {
  if (field.type === 'select') {
    return (
      <FormControl fullWidth size="medium" disabled={field.disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={value ?? field.default ?? ''}
          onChange={e => onChange(field.name, e.target.value as string)}
          onBlur={() => onBlur?.(field.name)}
        >
          {field.options?.map(opt => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === 'password') {
    return (
      <PasswordField
        label={field.label}
        value={value}
        onChange={v => onChange(field.name, v)}
        onBlur={() => onBlur?.(field.name)}
        disabled={field.disabled}
        required={required}
        error={error}
        helperText={helperText}
      />
    );
  }

  return (
    <TextField
      label={field.label}
      type={field.type === 'string' ? 'text' : field.type}
      value={value ?? ''}
      onChange={e => onChange(field.name, e.target.value)}
      onBlur={() => onBlur?.(field.name)}
      fullWidth
      size="medium"
      disabled={field.disabled}
      required={required}
      error={error}
      helperText={helperText ?? description ?? ''}
      placeholder={field.placeholder}
    />
  );
};

export const RadioCheckboxCardField = (
  props: FieldExtensionComponentProps<Record<string, unknown>>,
) => {
  const {
    onChange,
    formData,
    uiSchema,
    disabled,
    readonly,
    schema,
    rawErrors,
  } = props as any;

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  const options = useMemo(
    () => (uiSchema['ui:options']?.options ?? []) as OptionType[],
    [uiSchema],
  );

  const isEditMode = Boolean(disabled || readonly);

  const [selected, setSelected] = useState<string>('');
  const [values, setValues] = useState<FieldValues>({});
  const [replaceApi, setReplaceApi] = useState(false);
  const [replaceOAuth, setReplaceOAuth] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );

  const buildPayload = (
    authType: string,
    updated: FieldValues,
    nextReplaceApi: boolean,
    nextReplaceOAuth: boolean,
  ) => {
    const payload: Record<string, any> = {
      type: authType,
      replaceApi: authType === 'API Key' ? nextReplaceApi : false,
      replaceOAuth:
        authType === 'OAuth Client Credentials' ? nextReplaceOAuth : false,
    };

    Object.entries(updated).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload[key] = value;
      }
    });

    if (authType === 'API Key') {
      delete payload.tokenUrl;
      delete payload.clientId;
      delete payload.clientSecret;
      delete payload.oauthScope;

      // apiKey should be sent only if replace is checked
      if (!nextReplaceApi) {
        delete payload.apiKey;
      } else {
        // keep apiKey even if empty so form/schema handles required validation
        payload.apiKey = updated.apiKey ?? '';
      }

      // emit expiry only when valid
      const expiryValue = updated.apiKeyExpiresAt ?? '';
      const expiryValidation = parseAndValidateFutureDate(expiryValue);
      if (!expiryValidation.valid) {
        delete payload.apiKeyExpiresAt;
      }
    }

    if (authType === 'OAuth Client Credentials') {
      delete payload.apiKey;
      delete payload.apiKeyLocation;
      delete payload.apiKeyLocationName;
      delete payload.apiKeyExpiresAt;

      // clientId / clientSecret should be sent only if replace is checked
      if (!nextReplaceOAuth) {
        delete payload.clientId;
        delete payload.clientSecret;
      } else {
        // keep them even if empty so form/schema handles required validation
        payload.clientId = updated.clientId ?? '';
        payload.clientSecret = updated.clientSecret ?? '';
      }
    }

    return payload;
  };

  useEffect(() => {
    if (!formData) return;
    if (selected) return;

    let resolvedAuthType = '';
    let initialValues: FieldValues = {};
    let initialReplaceApi = false;
    let initialReplaceOAuth = false;

    if (typeof formData === 'string') {
      resolvedAuthType = formData;
    } else if ((formData as any)?.type) {
      resolvedAuthType = (formData as any).type;
      initialValues = { ...(formData as any) };

      initialReplaceApi = Boolean((formData as any)?.replaceApi);
      initialReplaceOAuth = Boolean((formData as any)?.replaceOAuth);

      delete initialValues.type;
      delete initialValues.replaceApi;
      delete initialValues.replaceOAuth;
    }

    if (resolvedAuthType === 'API Key') {
      delete initialValues.tokenUrl;
      delete initialValues.clientId;
      delete initialValues.clientSecret;
      delete initialValues.oauthScope;

      initialReplaceOAuth = false;
    }

    if (resolvedAuthType === 'OAuth Client Credentials') {
      delete initialValues.apiKey;
      delete initialValues.apiKeyLocation;
      delete initialValues.apiKeyLocationName;
      delete initialValues.apiKeyExpiresAt;

      initialReplaceApi = false;
    }

    setSelected(resolvedAuthType);
    setValues(initialValues);
    setReplaceApi(initialReplaceApi);
    setReplaceOAuth(initialReplaceOAuth);
    setTouchedFields({});

    onChange(
      buildPayload(
        resolvedAuthType,
        initialValues,
        initialReplaceApi,
        initialReplaceOAuth,
      ),
    );
  }, [formData, selected, onChange]);

  const handleChange = (name: string, value: string) => {
    const updated = { ...values, [name]: value };
    setValues(updated);

    onChange(buildPayload(selected, updated, replaceApi, replaceOAuth));
  };

  const handleBlur = (name: string) => {
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  const handleRadioChange = (value: string) => {
    if (isEditMode) return;

    setSelected(value);
    setValues({});
    setReplaceApi(false);
    setReplaceOAuth(false);
    setTouchedFields({});

    onChange(buildPayload(value, {}, false, false));
  };

  const active = options.find(o => o.value === selected);

  const infoText = active?.uiOptions?.infoText;
  const checkboxLabel = active?.uiOptions?.checkboxLabel;
  const fields = active?.fields ?? [];

  const location = fields.filter(f =>
    ['apiKeyLocation', 'apiKeyLocationName'].includes(f.name),
  );

  const apiKey = fields.find(f => f.name === 'apiKey');
  const expiry = fields.find(f => f.name === 'apiKeyExpiresAt');
  const tokenUrl = fields.find(f => f.name === 'tokenUrl');

  const oauthSecrets = fields.filter(f =>
    ['clientId', 'clientSecret'].includes(f.name),
  );

  const scope = fields.find(f => f.name === 'oauthScope');

  const expiryValue = values.apiKeyExpiresAt ?? '';
  const expiryValidation = parseAndValidateFutureDate(expiryValue);

  const showExpiryError =
    selected === 'API Key' &&
    touchedFields.apiKeyExpiresAt &&
    expiryValue.trim() !== '' &&
    !expiryValidation.valid;

  const showApiKeyRequiredError =
    selected === 'API Key' &&
    replaceApi &&
    !(values.apiKey ?? '').trim() &&
    (touchedFields.apiKey || Boolean(rawErrors?.length));

  return (
    <Box sx={{ backgroundColor, p: 2, borderRadius: 1 }}>
      {schema.title && (
        <Typography
          variant="h6"
          sx={{ marginBottom: 1, marginTop: 0 }}
          color={theme.palette.text.primary}
        >
          {schema.title}
        </Typography>
      )}

      {schema.description && (
        <Typography
          variant="body2"
          sx={{ marginBottom: 1 }}
          color={theme.palette.text.primary}
        >
          {schema.description}
        </Typography>
      )}

      {(schema.title || schema.description) && (
        <Divider sx={{ marginBottom: 1, marginTop: 0.5 }} />
      )}

      <FormControl>
        <RadioGroup
          row
          value={selected}
          onChange={e => handleRadioChange(e.target.value)}
          sx={{ mb: 2 }}
        >
          {options.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio disabled={isEditMode} />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <Box display="flex" flexDirection="column" gap={2}>
        {selected === 'API Key' && (
          <>
            {location.map(f => (
              <FieldInput
                key={f.name}
                field={f}
                value={values[f.name]}
                onChange={handleChange}
                onBlur={handleBlur}
                required={['apiKeyLocation', 'apiKeyLocationName'].includes(
                  f.name,
                )}
              />
            ))}

            {infoText && <InfoAlert text={infoText} sx={{ padding: 1 }} />}

            <FormControlLabel
              control={
                <Checkbox
                  checked={replaceApi}
                  onChange={() => {
                    const next = !replaceApi;
                    const updated = { ...values };

                    if (!next) {
                      delete updated.apiKey;
                    }

                    setReplaceApi(next);
                    setValues(updated);

                    onChange(
                      buildPayload(selected, updated, next, replaceOAuth),
                    );
                  }}
                />
              }
              label={checkboxLabel}
            />

            {replaceApi && apiKey && (
              <PasswordField
                label={apiKey.label}
                value={values[apiKey.name]}
                onChange={v => handleChange(apiKey.name, v)}
                onBlur={() => handleBlur(apiKey.name)}
                disabled={apiKey.disabled}
                required
                error={showApiKeyRequiredError}
                helperText={
                  showApiKeyRequiredError
                    ? 'This field is required.'
                    : undefined
                }
              />
            )}

            {expiry && (
              <FieldInput
                field={expiry}
                value={values[expiry.name]}
                onChange={handleChange}
                onBlur={handleBlur}
                required={false}
                error={showExpiryError}
                helperText={
                  showExpiryError ? expiryValidation.message : undefined
                }
              />
            )}
          </>
        )}

        {selected === 'OAuth Client Credentials' &&
          active?.value === 'OAuth Client Credentials' && (
            <>
              {tokenUrl && (
                <FieldInput
                  field={tokenUrl}
                  value={values[tokenUrl.name]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              )}

              {infoText && <InfoAlert text={infoText} sx={{ padding: 0 }} />}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={replaceOAuth}
                    onChange={() => {
                      const next = !replaceOAuth;
                      const updated = { ...values };

                      if (!next) {
                        delete updated.clientId;
                        delete updated.clientSecret;
                      }

                      setReplaceOAuth(next);
                      setValues(updated);

                      onChange(
                        buildPayload(selected, updated, replaceApi, next),
                      );
                    }}
                  />
                }
                label={checkboxLabel}
              />

              {replaceOAuth &&
                oauthSecrets.map(f => (
                  <PasswordField
                    key={f.name}
                    label={f.label}
                    value={values[f.name]}
                    onChange={v => handleChange(f.name, v)}
                    onBlur={() => handleBlur(f.name)}
                    disabled={f.disabled}
                    required
                  />
                ))}

              {scope && (
                <FieldInput
                  field={scope}
                  value={values[scope.name]}
                  onChange={handleChange}
                  helperText={scope.description ?? ''}
                  onBlur={handleBlur}
                  required={false}
                />
              )}
            </>
          )}
      </Box>
    </Box>
  );
};
